import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { oilTypeService } from "~/services/purchasing";
import type { OilTypeFormData } from "~/types/purchasing";

const QUERY_KEY = "oil_types";
const PRICES_QUERY_KEY = "oil_type_prices";

export function useOilTypes() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => oilTypeService.getAll(),
  });
}

export function useOilType(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => oilTypeService.getById(id!),
    enabled: !!id,
  });
}

export function useLowStockOilTypes() {
  return useQuery({
    queryKey: [QUERY_KEY, "low_stock"],
    queryFn: () => oilTypeService.getLowStock(),
  });
}

export function useCreateOilType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OilTypeFormData) => oilTypeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateOilType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<OilTypeFormData>;
    }) => oilTypeService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteOilType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => oilTypeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// Price hooks
export function useOilTypePriceHistory(oilTypeId: string | undefined) {
  return useQuery({
    queryKey: [PRICES_QUERY_KEY, oilTypeId],
    queryFn: () => oilTypeService.getPriceHistory(oilTypeId!),
    enabled: !!oilTypeId,
  });
}

export function useLatestOilPrice(
  oilTypeId: string | undefined,
  supplierId?: string
) {
  return useQuery({
    queryKey: [PRICES_QUERY_KEY, "latest", oilTypeId, supplierId],
    queryFn: () => oilTypeService.getLatestPrice(oilTypeId!, supplierId),
    enabled: !!oilTypeId,
  });
}

export function useAddOilTypePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      oil_type_id: string;
      supplier_id?: string;
      unit_price: number;
      currency: "CLP" | "USD";
      effective_date: string;
    }) => oilTypeService.addPrice(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRICES_QUERY_KEY, variables.oil_type_id],
      });
      queryClient.invalidateQueries({
        queryKey: [PRICES_QUERY_KEY, "latest", variables.oil_type_id],
      });
    },
  });
}
